// src/ui/ProfileDropdown.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navigateTo, ROUTES } from "../lib/nav";
import Button from "./Button.jsx";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/apiFetch";
import AvatarPickerModal from "../components/Avatars/AvatarPickerModal";
import { persistAvatarForCurrentUser } from "../lib/avatarPersistence";

export default function ProfileDropdown({
  user,
  onSignOut,
  onCustomizeStudio,
  onChoosePlan,
}) {
  const [open, setOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState("");
  const ref = useRef(null);
  const navigate = useNavigate();
  const { user: authUser, userProfile, profile } = useAuth() || {};

  // close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, [open]);

  const storedName =
    (typeof localStorage !== "undefined" && localStorage.getItem("mudo:userName")) || "";
  const storedEmail =
    (typeof localStorage !== "undefined" && localStorage.getItem("mudo:userEmail")) || "";

  const derivedEmail = user?.email || authUser?.email || storedEmail || "";
  const emailName = derivedEmail.includes("@")
    ? derivedEmail.split("@")[0].replace(/\./g, " ").trim()
    : "";
  const derivedName =
    user?.name ||
    user?.full_name ||
    userProfile?.full_name ||
    userProfile?.display_name ||
    profile?.display_name ||
    profile?.full_name ||
    authUser?.user_metadata?.full_name ||
    authUser?.user_metadata?.name ||
    storedName ||
    emailName ||
    "";

  const derivedAvatarUrl =
    user?.avatarUrl ||
    user?.avatar_url ||
    profile?.avatar_url ||
    null;

  const derivedAvatarEmoji = profile?.avatar_emoji || null;

  const name = derivedName.trim() || "Guest";
  const email = derivedEmail.trim() || "Not signed in";
  const initial = (name?.trim()?.[0] || email?.trim()?.[0] || "?").toUpperCase();

  const menuItems = [
    {
      label: "Switch to Visitor World",
      variant: "accent",
      onClick: () => {
        try {
          localStorage?.setItem("mudo:userRole", "visitor");
        } catch {
          // ignore storage failures
        }
        navigateTo(navigate, ROUTES.DISCOVER, {
          source: "ProfileDropdown:switch-to-visitor",
        });
      },
    },
    { label: "Customize my studio", onClick: onCustomizeStudio },
    { label: "Subscription / Billing", onClick: onChoosePlan },
    {
      label: "Terms & Policies",
      onClick: () =>
        navigateTo(navigate, ROUTES.TERMS, {
          source: "ProfileDropdown:terms",
        }),
    },
    { label: "Logout", onClick: () => onSignOut?.(), variant: "danger" },
  ];

  // shared “drawer pill” classes so it matches the left nav
  const drawerButtonBase =
    "flex items-center gap-2 rounded-xl border border-transparent " +
    "bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_20%)] " +
    "px-3 py-2.5 text-sm font-semibold text-[var(--mudo-text)] " +
    "transition hover:border-[var(--mudo-border)]";

  return (
    <div className="relative" ref={ref}>
      {/* trigger */}
      <Button
        type="button"
        variant="soft"
        onClick={() => setOpen((v) => !v)}
        className="h-10 w-10 rounded-full text-[var(--mudo-onPrimary)] border flex items-center justify-center text-sm font-semibold focus:ring-2 focus:outline-none"
        style={{
          backgroundColor: "var(--mudo-primary)",
          borderColor: "var(--mudo-primary)",
          color: "var(--mudo-onPrimary, #0b0b0b)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.28)",
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name || email}
      >
        {derivedAvatarUrl ? (
          <img
            src={derivedAvatarUrl}
            alt={name}
            className="h-full w-full rounded-full object-cover"
            loading="lazy"
            decoding="async"
            width={40}
            height={40}
          />
        ) : (
          <span>{derivedAvatarEmoji || initial}</span>
        )}
      </Button>

      {/* dropdown */}
      {open && (
        <div
          className="absolute right-0 mt-3 min-w-[280px] max-w-[320px] rounded-2xl border border-[var(--mudo-border)] shadow-2xl px-3 py-3 text-[var(--mudo-text)] bg-[var(--mudo-bg-soft)]"
        >
          {/* header */}
          <div className="flex items-center gap-3 px-1 pb-3 border-b border-[var(--mudo-border)]/70">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate uppercase">{name}</div>
              <div className="text-xs text-[var(--mudo-text-muted)] truncate">
                {email}
              </div>
            </div>
            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--mudo-border)] bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_10%)] text-sm font-semibold">
              {derivedAvatarUrl ? (
                <img
                  src={derivedAvatarUrl}
                  alt={name}
                  className="h-full w-full rounded-xl object-cover"
                  loading="lazy"
                  decoding="async"
                  width={44}
                  height={44}
                />
              ) : (
                <span>{derivedAvatarEmoji || initial || "?"}</span>
              )}
            </span>
          </div>

          {/* menu items */}
          <div className="mt-3 space-y-2">
            <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--mudo-text-muted)]">
              Studio
            </div>

            {menuItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className={
                    drawerButtonBase +
                    (item.variant === "danger"
                      ? " text-red-300"
                      : item.variant === "accent"
                      ? " border-[var(--mudo-primary)] bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary)] hover:border-[var(--mudo-primary)] hover:brightness-105"
                      : " text-[var(--mudo-text)]")
                }
              >
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>

          <AvatarPickerModal
            open={avatarOpen}
            initialUrl={derivedAvatarUrl}
            onClose={() => setAvatarOpen(false)}
            onSave={async (nextUrl) => {
              setAvatarOpen(false);
              setOpen(false);
              const res = await persistAvatarForCurrentUser({
                avatarUrl: nextUrl,
                avatarEmoji: nextUrl ? null : derivedAvatarEmoji,
              });
              if (!res?.ok) {
                console.warn("[ProfileDropdown] persist avatar failed", res?.error);
              }
              try {
                window.dispatchEvent(new Event("mudo:profile-refresh"));
              } catch {}
            }}
          />

          {/* promo code */}
          <div className="mt-4 space-y-1">
            <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--mudo-text-muted)]">
              Promo code
            </div>

            <div className="rounded-xl border border-[var(--mudo-border)] bg-[color-mix(in_lab,var(--mudo-bg-soft),transparent_15%)] p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="e.g. Enter your Promo Code"
                  className="flex-1 rounded-lg px-3 py-2 bg-transparent border border-[var(--mudo-border)]/80 text-sm text-[var(--mudo-text)] placeholder:text-[var(--mudo-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--mudo-primary)]"
                />
                <button
                  type="button"
                  disabled={promoBusy || !promoCode.trim()}
                  onClick={async () => {
                    setPromoMsg("");
                    setPromoBusy(true);
                    try {
                      const res = await apiFetch("/api/promo/apply", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ code: promoCode.trim() }),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok || !json?.ok) {
                        const msg = json?.message || json?.error || "Failed to apply code";
                        throw new Error(msg);
                      }
                      setPromoMsg(json?.promo ? `${json.promo.code} applied` : "Promo applied");
                      try {
                        window.dispatchEvent(
                          new Event("mudo:profile-refresh")
                        );
                      } catch {
                        // ignore
                      }
                    } catch (e) {
                      setPromoMsg(e?.message || "Could not apply code");
                    } finally {
                      setPromoBusy(false);
                    }
                  }}
                  className="px-3 py-2 rounded-lg text-sm font-semibold bg-[#F3A74D] text-black hover:brightness-105 disabled:opacity-60"
                >
                  {promoBusy ? "Applying…" : "Apply"}
                </button>
              </div>

              {promoMsg && (
                <div className="text-[12px] text-[var(--mudo-text-muted)]">
                  {promoMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
